/**
 * Network Error Class
 *
 * Why: Specialized handling for network-level failures
 * What: Error class for connectivity issues (no HTTP reponse)
 * BENEFIT:
 *
 *      - Distinguish network issue fromAPI errors
 *      - Appropriate retry behaviour
 *      -User-friendly offline handling
 *
 * Use Cases:
 *  - No internet connection
 *  - DNS resolution failure
 *  - SSL certificate errors
 *  - Connection refused
 *  - Request aborted (but not cancelled)
 */

import { AppError, type AppErrorOptions } from "./base/AppError";
import { ErrorCategory, ErrorSeverity } from "../types";
import { ERROR_CODES } from "../constants";
import { ERROR_MESSAGES } from "../constants";

/**
 * Options specific to network errors
 */
export interface NetworkErrorOptions extends Omit<
  AppErrorOptions,
  "statusCode"
> {
  /** Original error code from axios/fetch */
  originalCode?: string;
}
/**
 * Network Error
 *
 * Represents errors that occur at the network level.
 * before any HTTP response is received
 */
export class NetworkError extends AppError {
  /**
   * Original error code from the http client
   * Examples: 'ECONNREFUSED','ENOTFOUND','ETIMEOUT'
   */
  public readonly originalCode?: string;

  constructor(
    message: string = ERROR_MESSAGES.NETWORK_ERROR,
    options: NetworkErrorOptions = {},
  ) {
    super(message, {
      statusCode: 0, // No HTTP status for network errors
      code: options.code ?? ERROR_CODES.NETWORK_ERROR,
      category: options.category ?? ErrorCategory.NETWORK,
      severity: options.severity ?? ErrorSeverity.MEDIUM,
      isOperational: options.isOperational ?? true,
      isRetryable: options.isRetryable ?? true, // Network errors are usually retryable
      context: options.context,
      cause: options.cause,
    });
    this.originalCode = options.originalCode;
  }

  /**
   * Create error for connection refused
   * Server is not accepting connections
   */
  static connectionRefused(cause?: unknown): NetworkError {
    return new NetworkError(ERROR_MESSAGES.CONNECTION_REFUSED, {
      code: ERROR_CODES.NETWORK_CONNECTION_REFUSED,
      originalCode: "ECONNREFUSED",
      cause,
    });
  }

  /**
   * Create error for DNS failure
   * Domain name could not be resolved
   */
  static dnsFailure(cause?: unknown): NetworkError {
    return new NetworkError(
      "Unable to resolve server address. Please check your connection.",
      {
        code: ERROR_CODES.NETWORK_DNS_FAILED,
        originalCode: "ENOTFOUND",
        cause,
      },
    );
  }
  /**
   * Create error for SSL/TLS failure
   * Certificate validation failed
   */
  static sslError(cause?: unknown): NetworkError {
    return new NetworkError(`Secure connection could not be established`, {
      code: ERROR_CODES.NETWORK_SSL_ERROR,
      severity: ErrorSeverity.HIGH,
      cause,
    });
  }

  /**
   * Create generic network error from error code
   */
  static fromErrorCode(errorCode: string, cause?: unknown): NetworkError {
    switch (errorCode) {
      case "ECONNREFUSED":
        return NetworkError.connectionRefused(cause);
      case "ENOTFOUND":
        return NetworkError.dnsFailure(cause);
      case "ERR_NETWORK":
        return new NetworkError(ERROR_MESSAGES.NETWORK_ERROR, { cause });
      default:
        return new NetworkError(ERROR_MESSAGES.NETWORK_ERROR, {
          originalCode: errorCode,
          cause,
        });
    }
  }
  /**
   * Check if user appear to be offline
   *
   */
  public static isOffline(): boolean {
    return typeof navigator !== "undefined" && !navigator.onLine;
  }
}
